import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Download, Search, Lock, LogOut, ChevronRight, Pencil, Trash2, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["Chef", "Sous Chef", "Sommelier", "Restaurant Manager", "Front of House", "Bartender", "Owner", "Other"];

// Parse a raw Mailchimp JSON response body into structured diagnostics (read-only; no logic change).
const parseMcBody = (body) => {
  if (!body) return null;
  try {
    const j = typeof body === "string" ? JSON.parse(body) : body;
    return {
      title: j.title || null,
      detail: j.detail || null,
      status: j.status,
      errors: Array.isArray(j.errors) ? j.errors.map((e) => ({ field: e.field, message: e.message })) : [],
    };
  } catch {
    return null;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_pw") || "");
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", role: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toggleExpand = (key) => setExpanded((k) => (k === key ? null : key));

  const openEdit = (r) => {
    setEditForm({
      first_name: r.first_name || "", last_name: r.last_name || "", email: r.email || "",
      role: r.role || "", company: r.company || "",
    });
    setEditing(r);
  };

  const saveEdit = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim() || !editForm.role) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/registrations/${editing.id}`, editForm, {
        headers: { "X-Admin-Password": token },
      });
      const updated = res.data.registration;
      setRows((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
      setEditing(null);
      toast.success("Registration updated.");
    } catch (e) {
      toast.error("Failed to update registration.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/registrations/${deleteTarget.id}`, {
        headers: { "X-Admin-Password": token },
      });
      setRows((prev) => prev.filter((row) => row.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Registration deleted.");
    } catch (e) {
      toast.error("Failed to delete registration.");
    } finally {
      setDeleting(false);
    }
  };

  const mcStatus = (r) => {
    const d = r.mc_debug;
    if (d?.skipped) return { label: "SKIPPED", tone: "muted", code: null };
    if (d?.exception) return { label: "ERROR", tone: "bad", code: null };
    if (typeof d?.put_status === "number") {
      const ok = d.put_status === 200 || d.put_status === 204;
      return { label: String(d.put_status), tone: ok ? "good" : "bad", code: d.put_status };
    }
    if (r.sync?.mailchimp) return { label: "OK", tone: "good", code: 200 };
    return { label: "—", tone: "muted", code: null };
  };

  const fetchRows = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/registrations`, {
        headers: { "X-Admin-Password": token },
        params: { search: q },
      });
      setRows(res.data.registrations || []);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        sessionStorage.removeItem("admin_pw");
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchRows("");
  }, [token, fetchRows]);

  useEffect(() => {
    if (!authed) return;
    const id = setTimeout(() => fetchRows(search), 300);
    return () => clearTimeout(id);
  }, [search, authed, fetchRows]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem("admin_pw", pw);
      setToken(pw);
    } catch (err) {
      setLoginError("Incorrect password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_pw");
    setToken("");
    setAuthed(false);
    setRows([]);
  };

  const exportCsv = () => {
    window.open(`${API}/admin/registrations/export?password=${encodeURIComponent(token)}`, "_blank");
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 text-[#D4AF37]">
            <Lock className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.3em] font-sans-body">Admin Access</span>
          </div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3 font-sans-body">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="flush-input w-full py-3 text-lg mb-6"
            autoFocus
          />
          {loginError && <p data-testid="admin-login-error" className="text-sm text-red-400 mb-4">{loginError}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full bg-[#D4AF37] py-4 text-xs uppercase tracking-[0.2em] text-black hover:bg-[#F3E5AB] transition-colors"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans-body">Early Access</p>
            <h1 className="font-serif-display font-light text-4xl md:text-5xl">Registrations <span className="text-zinc-500">({rows.length})</span></h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-[#FAFAFA] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, role, company…"
            className="flush-input w-full pl-7 py-3 text-base"
          />
        </div>

        <div className="overflow-x-auto border-t border-white/10">
          <table className="w-full text-left" data-testid="registrations-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans-body">
                <th className="py-4 pr-4 font-normal">Name</th>
                <th className="py-4 pr-4 font-normal">Email</th>
                <th className="py-4 pr-4 font-normal">Role</th>
                <th className="py-4 pr-4 font-normal">Company</th>
                <th className="py-4 pr-4 font-normal">Source</th>
                <th className="py-4 pr-4 font-normal">Source Detail</th>
                <th className="py-4 pr-4 font-normal">Date</th>
                <th className="py-4 pr-4 font-normal">Mailchimp</th>
                <th className="py-4 pr-4 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="font-sans-body text-sm">
              {loading ? (
                <tr><td colSpan={9} className="py-10 text-zinc-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="py-10 text-zinc-500" data-testid="no-registrations">No registrations yet.</td></tr>
              ) : (
                rows.map((r, i) => {
                  const key = r.id || r.email || i;
                  const st = mcStatus(r);
                  const d = r.mc_debug || {};
                  const isOpen = expanded === key;
                  const toneClass = st.tone === "good" ? "text-[#D4AF37] border-[#D4AF37]/40"
                    : st.tone === "bad" ? "text-red-400 border-red-400/40"
                    : "text-zinc-500 border-white/10";
                  const hasDetail = !!r.mc_debug;
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid="registration-row">
                        <td className="py-4 pr-4">{r.first_name} {r.last_name}</td>
                        <td className="py-4 pr-4 text-zinc-400">{r.email}</td>
                        <td className="py-4 pr-4"><span className="text-[#D4AF37]">{r.role}</span></td>
                        <td className="py-4 pr-4 text-zinc-400">{r.company || "—"}</td>
                        <td className="py-4 pr-4">{r.source ? <span className="text-[#D4AF37]">{r.source}</span> : <span className="text-zinc-600">—</span>}</td>
                        <td className="py-4 pr-4 text-zinc-400">{r.source_detail || "—"}</td>
                        <td className="py-4 pr-4 text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                        <td className="py-4 pr-4">
                          <button
                            type="button"
                            data-testid="mc-status-btn"
                            onClick={() => hasDetail && toggleExpand(key)}
                            disabled={!hasDetail}
                            className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] tracking-wide ${toneClass} ${hasDetail ? "cursor-pointer hover:bg-white/[0.04]" : "cursor-default"}`}
                          >
                            {hasDetail && (
                              <ChevronRight className={`h-3 w-3 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            )}
                            {st.label}
                          </button>
                        </td>
                        <td className="py-4 pr-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              data-testid="edit-registration-btn"
                              onClick={() => openEdit(r)}
                              title="Edit"
                              className="p-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              data-testid="delete-registration-btn"
                              onClick={() => setDeleteTarget(r)}
                              title="Delete"
                              className="p-2 text-zinc-400 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && hasDetail && (
                        <tr className="bg-white/[0.02]" data-testid="mc-debug-row">
                          <td colSpan={9} className="px-4 py-6">
                            <div className="space-y-5 font-mono text-xs text-zinc-400">
                              {d.skipped && (
                                <div><span className="text-zinc-500">Skipped:</span> {d.reason} (api_key_present={String(d.api_key_present)}, server_prefix={String(d.server_prefix)}, list_id={String(d.list_id)})</div>
                              )}
                              {typeof d.put_status !== "undefined" && (() => {
                                const p = parseMcBody(d.put_body);
                                const ok = d.put_status === 200 || d.put_status === 204;
                                return (
                                  <div className="space-y-2" data-testid="mc-debug-member">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      <div><span className="text-zinc-500">HTTP Status:</span> <span className={ok ? "text-[#D4AF37]" : "text-red-400"}>{d.put_status}</span></div>
                                      {p?.title && <div><span className="text-zinc-500">Title:</span> <span className="text-red-300">{p.title}</span></div>}
                                    </div>
                                    {p?.detail && <div><span className="text-zinc-500">Detail:</span> {p.detail}</div>}
                                    {p?.errors?.length > 0 && (
                                      <div data-testid="mc-invalid-merge-fields">
                                        <span className="text-zinc-500">Invalid merge field(s):</span>
                                        <ul className="mt-1 list-disc list-inside text-red-300">
                                          {p.errors.map((e, ei) => (
                                            <li key={ei}><span className="text-[#D4AF37]">{e.field}</span>{e.message ? ` — ${e.message}` : ""}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div>
                                      <div className="text-zinc-500 mb-1">Raw Mailchimp response (member PUT):</div>
                                      <pre className="whitespace-pre-wrap break-all bg-black/40 border border-white/10 p-3">{d.put_body || "(empty)"}</pre>
                                    </div>
                                  </div>
                                );
                              })()}
                              {typeof d.tags_status !== "undefined" && (() => {
                                const tg = parseMcBody(d.tags_body);
                                const ok = d.tags_status === 200 || d.tags_status === 204;
                                return (
                                  <div className="space-y-2" data-testid="mc-debug-tags">
                                    <div><span className="text-zinc-500">Tags HTTP Status:</span> <span className={ok ? "text-[#D4AF37]" : "text-red-400"}>{d.tags_status}</span></div>
                                    {tg?.title && <div><span className="text-zinc-500">Title:</span> <span className="text-red-300">{tg.title}</span></div>}
                                    {tg?.detail && <div><span className="text-zinc-500">Detail:</span> {tg.detail}</div>}
                                    {tg?.errors?.length > 0 && (
                                      <div data-testid="mc-invalid-tags">
                                        <span className="text-zinc-500">Invalid tag(s):</span>
                                        <ul className="mt-1 list-disc list-inside text-red-300">
                                          {tg.errors.map((e, ti) => (
                                            <li key={ti}>{e.field ? <span className="text-[#D4AF37]">{e.field} </span> : null}{e.message}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div>
                                      <div className="text-zinc-500 mb-1">Raw Mailchimp response (tags):</div>
                                      <pre className="whitespace-pre-wrap break-all bg-black/40 border border-white/10 p-3">{d.tags_body || "(empty)"}</pre>
                                    </div>
                                  </div>
                                );
                              })()}
                              {d.exception && (
                                <div>
                                  <div className="text-red-400 mb-1">Exception: {d.exception}</div>
                                  <pre className="whitespace-pre-wrap break-all bg-black/40 border border-white/10 p-3">{d.traceback}</pre>
                                </div>
                              )}
                              {d.ts && <div className="text-zinc-600">Last sync attempt: {d.ts}</div>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none border-white/10 bg-[#141414] text-[#FAFAFA] sm:max-w-lg" data-testid="edit-registration-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display font-light text-3xl">Edit registration</DialogTitle>
            <DialogDescription className="text-zinc-500">Update this registration. Changes are saved directly to the database.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">First name</label>
                <input data-testid="edit-first-name" value={editForm.first_name}
                  onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))}
                  className="flush-input w-full py-2 text-base" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Last name</label>
                <input data-testid="edit-last-name" value={editForm.last_name}
                  onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))}
                  className="flush-input w-full py-2 text-base" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Email</label>
              <input data-testid="edit-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                className="flush-input w-full py-2 text-base" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Role</label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="edit-role" className="w-full rounded-none border-0 border-b border-white/10 bg-transparent px-0 py-2 text-base focus:ring-0 focus:border-[#D4AF37]">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-white/10 bg-[#141414] text-[#FAFAFA]">
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="rounded-none focus:bg-white/5 focus:text-[#D4AF37]">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Company <span className="text-zinc-700 lowercase tracking-normal">(optional)</span></label>
                <input data-testid="edit-company" value={editForm.company}
                  onChange={(e) => setEditForm((p) => ({ ...p, company: e.target.value }))}
                  className="flush-input w-full py-2 text-base" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-5 py-3 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#FAFAFA] transition-colors">Cancel</button>
            <button data-testid="save-edit-btn" onClick={saveEdit} disabled={saving}
              className="inline-flex items-center gap-2 bg-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.2em] text-black hover:bg-[#F3E5AB] transition-colors disabled:opacity-70">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none border-white/10 bg-[#141414] text-[#FAFAFA]" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display font-light text-2xl">Delete this registration?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/15 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-[#FAFAFA]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} disabled={deleting}
              className="rounded-none bg-red-500/90 text-white hover:bg-red-500">
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster position="bottom-right" />
    </div>
  );
}
